import { AppError } from '@app/errors/AppError'

import { removeStackInitiator } from '@app/utils/removeStackInitiator'

/** The request never produced a response, or produced one no reader could make sense of. */
export class RequestError extends AppError {
  name = 'InternalRequestError'
  description?: string
  body: string = ''

  static create(status: number, description: string, body: string) {
    let message = `${status}`
    if (description) message += ` - ${description}`
    if (body) message += `: ${body}`
    const err = new RequestError(message)
    err.status = status
    err.description = description
    err.body = body
    if (err.stack) {
      err.stack = removeStackInitiator(err.stack)
    }
    return err
  }
}

/** The internal api answered with a failure and said why — `response` holds what it said. */
export class ApiError extends AppError {
  name = 'InternalApiError'
  description?: string
  response?: InternalApiErrorResponse

  static create(status: number, description: string, response: InternalApiErrorResponse | undefined) {
    const err = new ApiError(`${status} - ${description}`)
    err.status = status
    err.description = description
    err.response = response
    if (err.stack) {
      err.stack = removeStackInitiator(err.stack)
    }
    return err
  }
}

export const isApiError = (e: unknown): e is ApiError => e instanceof ApiError

export const asApiError = (e: unknown) => (isApiError(e) ? e : null)

export const isRequestError = (e: unknown): e is RequestError => e instanceof RequestError

export const isNetworkError = (e: unknown): e is ApiError | RequestError => isApiError(e) || isRequestError(e)

export const asNetworkError = (e: unknown) => (isNetworkError(e) ? e : null)

/** What `serverMiddleware/internal/utils.ts` sends on a failure. `status` is numeric here, unlike v3's string. */
export type InternalApiErrorResponse = {
  errors: { name?: string; title?: string | null; status?: number }[]
}

/** Narrow an already-parsed body to the error envelope, or `undefined` if it isn't one. */
export const asInternalApiErrorResponse = (value: unknown): InternalApiErrorResponse | undefined =>
  value && typeof value === 'object' && Array.isArray((value as { errors?: unknown }).errors) ? (value as InternalApiErrorResponse) : undefined
